import countries from 'iso-3166-1';
import i18n from 'src/i18n';
import { getOrdinalSuffix } from 'src/utils/utils';

export const NAV_LINKS = [
  {
    to: '/',
    label: 'Home',
  },
  {
    to: '/about',
    label: 'About',
  },
];

export const API_BASE_URL: string | undefined = (import.meta as any).env
  .API_BASE_URL as string | undefined;

export const COUNTRY_CODES = countries.all().map((c) => ({
  name: c.country,
  value: c.alpha2,
}));

export const US_STATE_CODES = [
  'AA', 'AE', 'AL', 'AK', 'AP', 'AS', 'AZ', 'AR',
  'CA', 'CO', 'CT',
  'DE', 'DC',
  'FL', 'FM',
  'GA', 'GU',
  'HI',
  'ID', 'IL', 'IN', 'IA',
  'KS', 'KY',
  'LA',
  'ME', 'MH', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'MP',
  'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND',
  'OH', 'OK', 'OR',
  'PA', 'PR', 'PW',
  'RI',
  'SC', 'SD',
  'TN', 'TX',
  'UT',
  'VT', 'VI', 'VA',
  'WA', 'WV', 'WI', 'WY',
];

export interface CurrencySymbol {
  id: string;
  title: string;
  symbol: string;
}

export const currencySymbolsWithTitle: CurrencySymbol[] = [
  { id: 'usd', title: 'US Dollars ($)', symbol: '$' },
  { id: 'cad', title: 'Canadian Dollars (CA$)', symbol: 'CA$' },
  { id: 'brl', title: 'Brazilian Real (R$)', symbol: 'R$' },
  { id: 'hkd', title: 'Hong Kong Dollars (HK$)', symbol: 'HK$' },
  { id: 'nzd', title: 'New Zeland Dollars (NZ$)', symbol: 'NZ$' },
  { id: 'eur', title: 'Euros (€)', symbol: '€' },
  { id: 'aud', title: 'Australian Dollars (A$)', symbol: 'A$' },
  { id: 'gbp', title: 'Pounds Sterling (£)', symbol: '£' },
  { id: 'sgd', title: 'Singapore Dollars (S$)', symbol: 'S$' },
  { id: 'chf', title: 'Swiss Franc (Fr)', symbol: 'Fr' },
  {
    id: 'aed',
    title: 'United Arab Emirates dirham (د.إ)',
    symbol: 'د.إ',
  },
  { id: 'dkk', title: 'Danish Krone (kr)', symbol: 'kr' },
  { id: 'mxn', title: 'Mexican Pesos (MX$)', symbol: 'MX$' },
];

export const DATES = [
  ...Array.from({ length: 28 }, (_, i) => {
    const num = i + 1;
    return {
      id: num.toString(),
      name: `${num}${getOrdinalSuffix(num)}`,
    };
  }),
  { id: '-1', name: 'end of the month' },
];

export const DAY_NUMBERS = Array.from({ length: 4 }, (_, i) => {
  const num = i + 1;
  return {
    id: num.toString(),
    name: `${num}${getOrdinalSuffix(num)}`,
  };
});

// HQ Permission levels
export const HQ_PERMISSION_LEVELS = {
  MANAGE_ALL: 0,
  MANAGE_MY_LOCATIONS: 1,
  MANAGE_NONE: 2,
} as const;

export const defaultProfileFields = [
  { label: 'First Name', type: 'first_name' },
  { label: 'Last Name', type: 'last_name' },
  { label: 'Phone Number', type: 'phone_number' },
  { label: 'Timezone', type: 'time_zone' },
  { label: 'Gender', type: 'gender' },
  { label: 'Street Address', type: 'street_address' },
  { label: 'City', type: 'city' },
  { label: 'State', type: 'state' },
  { label: 'Zip Code', type: 'zip_code' },
  { label: 'Height', type: 'height' },
  { label: 'Birthdate', type: 'birthdate' },
  { label: 'About Me', type: 'about_me' },
  { label: 'System of Measurement', type: 'uses_metric' },
  { label: 'Location', type: 'location_id' },
  { label: 'Weight', type: 'weight' },
];

export const workoutPlanIndexOtherPlans = [
  {
    id: 'all',
    label:
      'Show ALL plans and calendars, even if they are also in a Collection.',
  },
  {
    id: 'nonCollection',
    label: 'Show only plans and calendars that are not in a Collection.',
  },
  { id: 'none', label: "Don't show any additional plans or calendars." },
];

export const daysOfTheWeek = [
  { id: 0, value: 'sunday', label: i18n.t('common.weekdays.sunday') },
  { id: 1, value: 'monday', label: i18n.t('common.weekdays.monday') },
  { id: 2, value: 'tuesday', label: i18n.t('common.weekdays.tuesday') },
  { id: 3, value: 'wednesday', label: i18n.t('common.weekdays.wednesday') },
  { id: 4, value: 'thursday', label: i18n.t('common.weekdays.thursday') },
  { id: 5, value: 'friday', label: i18n.t('common.weekdays.friday') },
  { id: 6, value: 'saturday', label: i18n.t('common.weekdays.saturday') },
];

export const planOptions = {
  planObjectives: [
    { id: 1, name: 'Improve Cardio' },
    { id: 2, name: 'Gain Mass' },
    { id: 3, name: 'Get Ripped' },
    { id: 4, name: 'Gain Strength' },
    { id: 5, name: 'Lose Weight' },
    { id: 6, name: 'Get Toned' },
  ],
  difficultyLevels: [
    { name: 'Beginner', id: 1 },
    { name: 'Intermediate', id: 2 },
    { name: 'Expert', id: 3 },
  ],
  planTypes: [
    { name: 'Free', id: 'false' },
    { name: 'PRO', id: 'true' },
    { name: 'For Sale', id: 'for_sale' },
  ],
  genders: [
    { name: 'Male', id: 1 },
    { name: 'Female', id: 2 },
    { name: 'Gender Neutral', id: 0 },
  ],
  days: [
    { id: 1, name: 'One' },
    { id: 2, name: 'Two' },
    { id: 3, name: 'Three' },
    { id: 4, name: 'Four' },
    { id: 5, name: 'Five' },
    { id: 6, name: 'Six' },
    { id: 7, name: 'Seven' },
  ],
  wods: [{ name: 'Show WOD?', id: 'false' }],
};

export const SCORING_TYPES = {
  time: 'Time',
  rounds_reps: 'Rounds / Reps',
  reps: 'Reps',
  volume: 'Load',
  calories: 'Calories',
  points: 'Points',
  meters: 'Meters',
  centimeters: 'Centimeters',
  inches: 'Inches',
  feet: 'Feet',
};

export const Difficulties = [
  { name: 'None', id: '' },
  { name: 'Very Easy', id: 0 },
  { name: 'Beginner', id: 1 },
  { name: 'Intermediate', id: 2 },
  { name: 'Expert', id: 3 },
  { name: 'Insane', id: 4 },
];

export const visitStatuses = [
  { id: 'reserved', name: 'Reserved' },
  { id: 'registered', name: 'Registered' },
  { id: 'completed', name: 'Completed' },
  { id: 'noshowed', name: 'No Show' },
  { id: 'late_cancelled', name: 'Late Cancellation' },
  { id: 'cancelled', name: 'Cancelled' },
];
